import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api, { formatApiError } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { FileText, FileSignature, Receipt, ExternalLink, CheckCircle2, Circle, X } from 'lucide-react';
import { toast } from 'sonner';

const fmtGBP = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

const STAGE_COLOURS = {
    pre_sales:       'border-slate-300  bg-slate-50',
    proposal_sent:   'border-amber-300  bg-amber-50',
    contract_signed: 'border-violet-300 bg-violet-50',
    in_build:        'border-indigo-300 bg-indigo-50',
    client_review:   'border-fuchsia-300 bg-fuchsia-50',
    handover:        'border-cyan-300   bg-cyan-50',
    live:            'border-emerald-300 bg-emerald-50',
    renewal_due:     'border-rose-300   bg-rose-50',
};

export default function Projects() {
    const [board, setBoard] = useState(null);
    const [openClientId, setOpenClientId] = useState(null);

    const load = useCallback(async () => {
        try { const r = await api.get('/projects/board'); setBoard(r.data); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load board'); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const onDragEnd = async (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        // Optimistic UI update
        setBoard(prev => {
            if (!prev) return prev;
            const cols = prev.columns.map(c => ({ ...c, cards: [...c.cards] }));
            const fromCol = cols.find(c => c.key === source.droppableId);
            const toCol   = cols.find(c => c.key === destination.droppableId);
            if (!fromCol || !toCol) return prev;
            const [card] = fromCol.cards.splice(source.index, 1);
            card.stage = destination.droppableId;
            toCol.cards.splice(destination.index, 0, card);
            return { ...prev, columns: cols };
        });

        try {
            await api.patch(`/projects/${draggableId}/stage`, { stage: destination.droppableId });
            toast.success('Stage updated');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
            load();
        }
    };

    if (!board) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;
    const totalCards = board.columns.reduce((s, c) => s + c.cards.length, 0);

    return (
        <div data-testid="projects-page" className="bm-fade-in flex flex-col h-[calc(100vh-0px)]">
            <PageHeader title="Projects" description={`${totalCards} client projects in flight — drag a card to change stage.`} />
            <div className="flex-1 overflow-x-auto px-6 pb-6">
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="flex gap-3 min-w-max h-full">
                        {board.columns.map(col => (
                            <BoardColumn key={col.key} column={col} onCardClick={setOpenClientId} />
                        ))}
                    </div>
                </DragDropContext>
            </div>
            <ProjectDrawer
                clientId={openClientId}
                onClose={() => setOpenClientId(null)}
                onChanged={load}
            />
        </div>
    );
}


function BoardColumn({ column, onCardClick }) {
    return (
        <div className={`w-[300px] flex-shrink-0 rounded-xl border-2 ${STAGE_COLOURS[column.key] || 'border-slate-200 bg-slate-50'} flex flex-col`}>
            <div className="px-4 py-3 border-b border-black/10">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-[13.5px] font-semibold uppercase tracking-wide">{column.label}</h3>
                    <div className="flex items-center gap-1.5">
                        {column.sla_days > 0 && (
                            <span className="text-[10px] text-[color:var(--bm-text-muted)] bg-white/60 rounded px-1.5 py-0.5" title="SLA target">
                                SLA {column.sla_days}d
                            </span>
                        )}
                        <span className="text-[11px] font-semibold bg-white/60 rounded-full px-2 py-0.5">{column.cards.length}</span>
                    </div>
                </div>
            </div>
            <Droppable droppableId={column.key}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 p-3 space-y-2 overflow-y-auto transition ${snapshot.isDraggingOver ? 'bg-white/40' : ''}`}
                        data-testid={`col-${column.key}`}
                    >
                        {column.cards.map((card, idx) => (
                            <Draggable key={card.id} draggableId={card.id} index={idx}>
                                {(p, s) => (
                                    <div
                                        ref={p.innerRef}
                                        {...p.draggableProps}
                                        {...p.dragHandleProps}
                                        onClick={() => onCardClick(card.id)}
                                        className={`bg-white rounded-lg border border-[color:var(--bm-border)] p-3 cursor-pointer hover:shadow-md transition ${s.isDragging ? 'shadow-lg rotate-1' : ''}`}
                                        data-testid={`project-card-${card.id}`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <Avatar name={card.company_name} />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-[13px] truncate">{card.company_name}</div>
                                                {card.trading_name && <div className="text-[11px] text-[color:var(--bm-text-subtle)] truncate">{card.trading_name}</div>}
                                            </div>
                                        </div>
                                        <div className="mt-2.5 flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-3 text-[11px] text-[color:var(--bm-text-muted)]">
                                                <span className="flex items-center gap-1">
                                                    <CheckCircle2 size={12} className={card.checklist_done === card.checklist_total && card.checklist_total > 0 ? 'text-emerald-600' : ''} />
                                                    {card.checklist_done}/{card.checklist_total}
                                                </span>
                                                <span className="flex items-center gap-1" title="Open invoices">
                                                    <Receipt size={12} />{card.open_invoices}
                                                </span>
                                                <span className="flex items-center gap-1" title="Contract">
                                                    <FileSignature size={12} className={card.contract_status === 'signed' ? 'text-emerald-600' : ''} />
                                                    {card.contract_status === 'signed' ? 'Signed' : (card.contract_status === 'draft' ? 'Draft' : '—')}
                                                </span>
                                            </div>
                                            <SlaPill days={card.days_in_stage} sla={card.sla_days} status={card.sla_status} />
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        {column.cards.length === 0 && (
                            <div className="text-center text-[11.5px] text-[color:var(--bm-text-subtle)] py-8 italic">Drop cards here</div>
                        )}
                    </div>
                )}
            </Droppable>
        </div>
    );
}


function Avatar({ name }) {
    const initials = (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    const hue = (name || 'X').charCodeAt(0) * 31 % 360;
    return (
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
            style={{ background: `hsl(${hue}, 60%, 45%)` }}>
            {initials}
        </div>
    );
}


function SlaPill({ days, sla, status }) {
    if (days == null) return null;
    const dayLabel = days === 0 ? 'today' : days === 1 ? '1d' : `${days}d`;
    if (sla == null || sla <= 0) {
        return <span className="text-[10.5px] bm-pill bg-white/60 border border-black/5 text-[color:var(--bm-text-muted)]">{dayLabel}</span>;
    }
    const klass = status === 'over'
        ? 'bg-red-100 border-red-300 text-red-700'
        : status === 'warn'
            ? 'bg-amber-100 border-amber-300 text-amber-700'
            : 'bg-emerald-50 border-emerald-200 text-emerald-700';
    const icon = status === 'over' ? '🔴' : status === 'warn' ? '⚠️' : '';
    return (
        <span className={`text-[10.5px] bm-pill border font-semibold ${klass}`} title={`${days} of ${sla}-day SLA`}>
            {icon}{icon ? ' ' : ''}{dayLabel} / {sla}d
        </span>
    );
}


// ── Slide-over project detail drawer ──────────────────────────────────────────

function ProjectDrawer({ clientId, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [noteBody, setNoteBody] = useState('');
    const [posting, setPosting] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        if (!clientId) return;
        try { const r = await api.get(`/projects/${clientId}`); setData(r.data); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    }, [clientId]);
    useEffect(() => { setData(null); load(); }, [load]);

    if (!clientId) return null;

    const toggle = async (idx, done) => {
        const opt = { ...data };
        opt.checklist = opt.checklist.map((it, i) => i === idx ? { ...it, done } : it);
        setData(opt);
        try {
            await api.patch(`/projects/${clientId}/checklist`, { index: idx, done });
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed');
            load();
        }
    };

    const addNote = async () => {
        if (!noteBody.trim()) return;
        setPosting(true);
        try {
            const r = await api.post(`/projects/${clientId}/notes`, { body: noteBody });
            setData(d => ({ ...d, notes: [r.data.note, ...(d.notes || [])] }));
            setNoteBody('');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
        finally { setPosting(false); }
    };

    return (
        <Sheet open={!!clientId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" className="w-[640px] sm:max-w-[640px] overflow-y-auto" data-testid="project-drawer">
                <SheetHeader>
                    <div className="flex items-start justify-between">
                        <div>
                            <SheetTitle className="font-display text-[18px]">
                                {data?.client?.company_name || '...'}
                            </SheetTitle>
                            {data?.stage_label && (
                                <div className="mt-1.5 inline-flex items-center gap-2">
                                    <span className="bm-pill bg-indigo-50 border border-indigo-200 text-indigo-700">
                                        {data.stage_label}
                                    </span>
                                    <SlaPill days={data.days_in_stage} sla={data.sla_days}
                                        status={data.sla_days > 0 && data.days_in_stage != null
                                            ? (data.days_in_stage / data.sla_days >= 1 ? 'over' : data.days_in_stage / data.sla_days >= 0.8 ? 'warn' : 'ok')
                                            : null} />
                                </div>
                            )}
                        </div>
                        {data && (
                            <Button variant="outline" size="sm" className="h-8" onClick={() => navigate(`/clients/${clientId}`)} data-testid="drawer-open-client">
                                <ExternalLink size={13} className="mr-1.5" /> Open client record
                            </Button>
                        )}
                    </div>
                </SheetHeader>
                {!data && <div className="text-[color:var(--bm-text-muted)] mt-6">Loading…</div>}
                {data && (
                    <div className="mt-5 space-y-6 pb-12">
                        {/* Checklist */}
                        <Section title="Stage checklist">
                            <div className="space-y-1.5">
                                {data.checklist.map((item, i) => (
                                    <label key={i} className="flex items-center gap-2 text-[13px] cursor-pointer hover:bg-[color:var(--bm-bg-mute)] rounded px-2 py-1.5">
                                        <input type="checkbox" checked={item.done} onChange={e => toggle(i, e.target.checked)} className="cursor-pointer" data-testid={`drawer-check-${i}`} />
                                        <span className={item.done ? 'line-through text-[color:var(--bm-text-subtle)]' : ''}>{item.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Section>

                        {/* Delivery info */}
                        <Section title="Delivery & technical info">
                            <DeliverySummary info={data.delivery_info} />
                        </Section>

                        {/* Linked records */}
                        <Section title={`Proposals (${data.proposals.length})`}>
                            <LinkedTable items={data.proposals} cols={[
                                { k: 'proposal_number', label: '#', mono: true },
                                { k: 'title', label: 'Title' },
                                { k: 'status', label: 'Status', pill: true },
                                { k: 'value', label: 'Value', money: true, align: 'right' },
                            ]} />
                        </Section>
                        <Section title={`Contracts (${data.contracts.length})`}>
                            <LinkedTable items={data.contracts} cols={[
                                { k: 'contract_number', label: '#', mono: true },
                                { k: 'title', label: 'Title' },
                                { k: 'status', label: 'Status', pill: true },
                                { k: 'value', label: 'Value', money: true, align: 'right' },
                            ]} />
                        </Section>
                        <Section title={`Invoices (${data.invoices.length})`}>
                            <LinkedTable items={data.invoices} cols={[
                                { k: 'invoice_number', label: '#', mono: true },
                                { k: 'due_date', label: 'Due' },
                                { k: 'status', label: 'Status', pill: true },
                                { k: 'amount', label: 'Total', money: true, align: 'right' },
                                { k: 'balance_due', label: 'Balance', money: true, align: 'right' },
                            ]} />
                        </Section>

                        {/* Notes */}
                        <Section title="Project notes">
                            <Textarea
                                value={noteBody}
                                onChange={e => setNoteBody(e.target.value)}
                                placeholder="Add a project note (Cmd+Enter to post)"
                                onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') addNote(); }}
                                className="min-h-[60px]"
                                data-testid="drawer-note-input"
                            />
                            <div className="flex justify-end mt-2">
                                <Button onClick={addNote} disabled={posting || !noteBody.trim()} size="sm" className="h-8" style={{ background: 'var(--bm-primary)' }} data-testid="drawer-note-post">
                                    {posting ? 'Posting…' : 'Post note'}
                                </Button>
                            </div>
                            <div className="mt-3 space-y-2">
                                {data.notes.length === 0 && <div className="text-[12px] text-[color:var(--bm-text-subtle)] italic">No notes yet.</div>}
                                {data.notes.map((n, i) => (
                                    <div key={i} className="bg-[color:var(--bm-bg-mute)] rounded-lg p-3">
                                        <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mb-1 flex items-center gap-2">
                                            <span className="font-semibold">{n.author_name || 'Unknown'}</span>
                                            <span>·</span>
                                            <span>{new Date(n.ts).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'2-digit', hour:'2-digit', minute:'2-digit' })}</span>
                                        </div>
                                        <div className="text-[13px] whitespace-pre-wrap">{n.body}</div>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}


function Section({ title, children }) {
    return (
        <div>
            <h4 className="font-display text-[12.5px] font-semibold uppercase tracking-wide text-[color:var(--bm-text-muted)] mb-2">{title}</h4>
            {children}
        </div>
    );
}


function DeliverySummary({ info }) {
    const fields = [
        ['Platform URL', info?.platform_url],
        ['Admin login', info?.admin_login_url],
        ['Azure tenant', info?.azure_tenant_id],
        ['Azure client ID', info?.azure_client_id],
        ['Resend domain', info?.resend_domain],
        ['DNS provider', info?.dns_provider],
        ['DB name', info?.db_name],
        ['GitHub ref', info?.github_ref],
        ['Environment', info?.deployment_env],
    ].filter(([, v]) => v);
    if (fields.length === 0) {
        return <div className="text-[12px] text-[color:var(--bm-text-subtle)] italic">Add details on the client → Delivery tab.</div>;
    }
    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
            {fields.map(([k, v]) => (
                <div key={k} className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{k}</div>
                    <div className="font-mono text-[11.5px] truncate">{v}</div>
                </div>
            ))}
            {info?.internal_notes && (
                <div className="col-span-2 mt-2">
                    <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Notes</div>
                    <div className="text-[12.5px] whitespace-pre-wrap">{info.internal_notes}</div>
                </div>
            )}
        </div>
    );
}


function LinkedTable({ items, cols }) {
    if (!items || items.length === 0) {
        return <div className="text-[12px] text-[color:var(--bm-text-subtle)] italic">None.</div>;
    }
    return (
        <div className="bg-white rounded-lg border border-[color:var(--bm-border)] overflow-x-auto">
            <table className="w-full text-[12px]">
                <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                    <tr>{cols.map(c => <th key={c.k} className={`px-2.5 py-1.5 font-medium text-${c.align || 'left'}`}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                    {items.slice(0, 20).map(it => (
                        <tr key={it.id} className="border-t border-[color:var(--bm-border)]/60">
                            {cols.map(c => {
                                const v = it[c.k];
                                let display = v;
                                if (c.money) display = v != null ? fmtGBP(v) : '—';
                                if (c.pill && v) {
                                    return <td key={c.k} className={`px-2.5 py-1.5 text-${c.align || 'left'}`}>
                                        <span className="bm-pill bg-slate-100 border border-slate-200 text-slate-700 capitalize">{String(v).replace('_', ' ')}</span>
                                    </td>;
                                }
                                return <td key={c.k} className={`px-2.5 py-1.5 text-${c.align || 'left'} ${c.mono ? 'font-mono text-[11px]' : ''}`}>{display ?? '—'}</td>;
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
